import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, ChevronLeft, ChevronRight, FileText, ImagePlus, Info, Loader2, X, Gavel, Tag, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, LER, PHYSICAL_STATES, REGIONS } from "@/lib/data";
import { validRut } from "@/lib/ops";
import { errMsg, fileUrl, useApi } from "@/lib/api";

const STEPS = ["Empresa Generadora", "Caracterización del Residuo", "Ubicación y Retiro", "Fotos y Certificados", "Modelo Económico"];
const PICKUP_NOTE = "Retiro a cargo del comprador/gestor";
const INITIAL = { company_id: "", title: "", category: "", ler_code: "", material_tag: "", weight: "", unit: "t", physical_state: "", weighed_calibrated: false, condition: "", description: "", region: "Región Metropolitana", comuna: "Pudahuel", pickup_hours: "Lun a Vie. 9:00 - 17:00 hrs", pickup_note: PICKUP_NOTE, rep_priority: false, photos: [], certificates: [], mode: "venta", price: "", starting_bid: "", auction_hours: 48 };

const Field = ({ label, children }) => <div className="space-y-2"><Label className="text-slate-700">{label}</Label>{children}</div>;

const Pick = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

const StepGen = ({ f, set, companies }) => {
  if (companies === null) return <Loader2 className="w-6 h-6 animate-spin text-[#00b37a]" />;
  if (!companies.length) {
    return (
      <div className="text-center py-8 space-y-4" data-testid="publish-no-companies">
        <p className="text-slate-600">Para publicar necesitas al menos una Empresa Generadora asociada y validada.</p>
        <Button asChild className="rounded-full"><Link to="/mis-empresas" data-testid="publish-add-company-link">Asociar empresa</Link></Button>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="publish-wizard-step-1">
      <Label className="text-slate-700">Selecciona la empresa generadora del lote</Label>
      {companies.map((c) => {
        const r = c.resumen;
        return (
          <button type="button" key={c.id} onClick={() => set("company_id", c.id)} data-testid={`publish-company-${c.id}`}
            className={`w-full text-left p-4 rounded-2xl border-2 transition-colors ${f.company_id === c.id ? "border-[#00f2a1] bg-[#00f2a1]/10" : "border-slate-200 hover:border-slate-300"}`}>
            <p className="font-semibold text-[#05130e]">{r.razon_social}</p>
            <p className="text-xs font-mono text-slate-500">RUT {r.rut} · Establecimiento VU {r.id_establecimiento_vu}{r.nombre_establecimiento ? ` · ${r.nombre_establecimiento}` : ""}</p>
            {r.rut && !validRut(r.rut) && <p className="flex items-center gap-1 text-xs text-amber-700 mt-1" data-testid="publish-gen-rut-warning"><AlertTriangle className="w-3.5 h-3.5" />El dígito verificador del RUT no coincide.</p>}
          </button>
        );
      })}
      <Link to="/mis-empresas" className="inline-block text-sm text-[#00915f] hover:underline">+ Asociar otra empresa</Link>
    </div>
  );
};

const LerPick = ({ f, set }) => (
  <div className="sm:col-span-2 space-y-3">
    <Label className="text-slate-700">Tipo y clasificación del residuo (código LER)</Label>
    <div className="grid gap-2" data-testid="publish-ler-options">
      {(LER[f.category] || []).map(([code, label, haz]) => (
        <button type="button" key={code} onClick={() => { set("ler_code", code); if (!f.material_tag || f.material_auto) { set("material_tag", label.split(" (")[0].slice(0, 60)); set("material_auto", true); } }} data-testid={`publish-ler-${code.replace(/[\s*]/g, "")}`}
          className={`text-left flex items-start gap-3 p-3 rounded-xl border-2 transition-colors ${f.ler_code === code ? "border-[#00f2a1] bg-[#00f2a1]/10" : "border-slate-200 hover:border-slate-300"}`}>
          <span className="font-mono text-xs font-semibold text-[#05130e] bg-white border border-slate-200 rounded-md px-2 py-1 shrink-0">{code}</span>
          <span className="text-sm text-slate-700 flex-1">{label}</span>
          <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full shrink-0 ${haz ? "bg-red-50 text-red-700" : "bg-[#00f2a1]/15 text-[#007a50]"}`}>{haz ? "Peligroso" : "No peligroso"}</span>
        </button>
      ))}
    </div>
    {LER[f.category]?.find(([c]) => c === f.ler_code)?.[2] && <p className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-xl p-3"><AlertTriangle className="w-4 h-4 shrink-0" />Residuo peligroso: su traslado se declara en SIDREP y solo puede retirarlo un gestor con autorización sanitaria para residuos peligrosos.</p>}
  </div>
);

const Step1 = ({ f, set }) => (
  <div className="grid sm:grid-cols-2 gap-5" data-testid="publish-wizard-step-characterization">
    <div className="sm:col-span-2"><Field label="Título del lote"><Input className="h-11" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Ej: Recortes de cobre grado 1" data-testid="publish-title-input" /></Field></div>
    <div className="sm:col-span-2"><Field label="Familia de residuo"><Pick value={f.category} onChange={(v) => { set("category", v); set("ler_code", ""); if (f.material_auto) set("material_tag", ""); }} placeholder="Selecciona" testId="publish-category-select" options={CATEGORIES.map((c) => ({ value: c.id, label: c.name }))} /></Field></div>
    {f.category && <LerPick f={f} set={set} />}
    <Field label="Nombre comercial del material"><Input className="h-11" value={f.material_tag} onChange={(e) => { set("material_tag", e.target.value); set("material_auto", false); }} placeholder="Ej: HDPE Inyección" data-testid="publish-material-input" /></Field>
    <Field label="Estado físico"><Pick value={f.physical_state} onChange={(v) => set("physical_state", v)} placeholder="Selecciona" testId="publish-state-select" options={PHYSICAL_STATES.map((s) => ({ value: s, label: s }))} /></Field>
    <Field label="Peso exacto">
      <div className="flex gap-2">
        <Input className="h-11 font-mono" type="number" min="0" step="0.001" value={f.weight} onChange={(e) => set("weight", e.target.value)} data-testid="publish-weight-input" />
        <Pick value={f.unit} onChange={(v) => set("unit", v)} testId="publish-unit-select" options={[{ value: "t", label: "Toneladas" }, { value: "kg", label: "Kilogramos" }]} />
      </div>
    </Field>
    <label className="flex items-start gap-3 text-sm text-slate-700 self-end pb-2">
      <Checkbox checked={f.weighed_calibrated} onCheckedChange={(v) => set("weighed_calibrated", !!v)} className="mt-0.5" data-testid="publish-scale-checkbox" />Pesado mediante báscula calibrada
    </label>
    <label className="sm:col-span-2 flex items-start gap-3 text-sm text-slate-700 bg-[#f4f7f6] rounded-xl p-4">
      <Checkbox checked={f.rep_priority} onCheckedChange={(v) => set("rep_priority", !!v)} className="mt-0.5" data-testid="publish-rep-checkbox" />
      Es un Producto Prioritario Ley REP 20.920 (aceites lubricantes, baterías, aparatos eléctricos y electrónicos, envases y embalajes industriales, neumáticos)
    </label>
    <div className="sm:col-span-2"><Field label="Descripción (opcional)"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Composición, embalaje, frecuencia de generación..." data-testid="publish-description-input" /></Field></div>
  </div>
);

const Step2 = ({ f, set }) => (
  <div className="grid sm:grid-cols-2 gap-5" data-testid="publish-wizard-step-2">
    <Field label="Región"><Pick value={f.region} onChange={(v) => { set("region", v); set("comuna", ""); }} placeholder="Selecciona región" testId="publish-region-select" options={Object.keys(REGIONS).map((r) => ({ value: r, label: r }))} /></Field>
    <Field label="Comuna"><Pick value={f.comuna} onChange={(v) => set("comuna", v)} placeholder={f.region ? "Selecciona comuna" : "Primero elige región"} testId="publish-comuna-select" options={(REGIONS[f.region] || []).map((c) => ({ value: c, label: c }))} /></Field>
    <div className="sm:col-span-2"><Field label="Horario de retiro"><Input className="h-11" value={f.pickup_hours} onChange={(e) => set("pickup_hours", e.target.value)} placeholder="Ej: Lun a Vie. 9:00 - 17:00 hrs" data-testid="publish-hours-input" /></Field></div>
    <p className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700 bg-[#f4f7f6] rounded-xl p-3" data-testid="publish-pickup-note"><Info className="w-4 h-4 text-[#00b37a]" />Nota: {PICKUP_NOTE}</p>
  </div>
);

const Uploader = ({ label, hint, accept, kind, f, set, icon: Icon }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [names, setNames] = useState({});
  const onFiles = async (files) => {
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api("post", "/uploads", fd);
        setNames((n) => ({ ...n, [data.id]: data.original_filename }));
        set(kind, (prev) => [...prev, data.id]);
      } catch (e) {
        toast.error(`${file.name}: ${errMsg(e)}`);
      }
    }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <Label className="text-slate-700">{label}</Label>
      <label className="flex flex-col items-center justify-center gap-2 h-36 rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#00c487] hover:bg-[#00f2a1]/5 cursor-pointer transition-colors" data-testid={`publish-upload-${kind}`}>
        {busy ? <Loader2 className="w-6 h-6 animate-spin text-[#00b37a]" /> : <Icon className="w-6 h-6 text-slate-400" />}
        <span className="text-sm text-slate-600">{busy ? "Subiendo..." : "Haz clic para subir"}</span>
        <span className="text-xs text-slate-400">{hint}</span>
        <input type="file" multiple accept={accept} className="hidden" disabled={busy} onChange={(e) => { onFiles([...e.target.files]); e.target.value = ""; }} data-testid={`publish-file-input-${kind}`} />
      </label>
      <div className="flex flex-wrap gap-3">
        {f[kind].map((id) => (
          <div key={id} className="relative group" data-testid={`uploaded-${kind}-item`}>
            {kind === "photos" ? <img src={fileUrl(id)} alt="" className="w-20 h-20 rounded-lg object-cover" /> : <span className="flex items-center gap-1 text-xs bg-slate-100 rounded-lg px-3 py-2"><FileText className="w-4 h-4" />{names[id] || "certificado.pdf"}</span>}
            <button type="button" onClick={() => set(kind, (prev) => prev.filter((x) => x !== id))} className="absolute -top-2 -right-2 bg-[#05130e] text-white rounded-full p-0.5" aria-label="Quitar"><X className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Step3 = (p) => (
  <div className="grid md:grid-cols-2 gap-6" data-testid="publish-wizard-step-3">
    <Uploader {...p} kind="photos" label="Fotos del lote" hint="JPG, PNG o WEBP · máx. 10 MB" accept="image/jpeg,image/png,image/webp" icon={ImagePlus} />
    <Uploader {...p} kind="certificates" label="Certificados de análisis (opcional)" hint="PDF o imagen · otorga badge de verificación" accept="application/pdf,image/*" icon={FileText} />
  </div>
);

const MODE_OPTS = [
  { id: "subasta", icon: Gavel, t: "Subasta rápida", d: "Ofertas en vivo, cierre en 24–72 h" },
  { id: "venta", icon: Tag, t: "Precio fijo", d: "Venta directa al primer comprador" },
  { id: "donacion", icon: HeartHandshake, t: "Donación / Retiro sin costo", d: "Para liceos técnicos, artesanos y makers" },
];

const Step4 = ({ f, set }) => (
  <div className="space-y-6" data-testid="publish-wizard-step-4">
    <div className="grid md:grid-cols-3 gap-4">
      {MODE_OPTS.map((m) => (
        <button type="button" key={m.id} onClick={() => set("mode", m.id)} data-testid={`publish-mode-${m.id}`}
          className={`text-left p-5 rounded-2xl border-2 transition-colors ${f.mode === m.id ? "border-[#00f2a1] bg-[#00f2a1]/10" : "border-slate-200 hover:border-slate-300"}`}>
          <m.icon className="w-6 h-6 text-[#00b37a] mb-3" /><p className="font-semibold text-[#05130e]">{m.t}</p><p className="text-xs text-slate-500 mt-1">{m.d}</p>
        </button>
      ))}
    </div>
    {f.mode === "venta" && <Field label="Precio total (CLP)"><Input className="h-11 font-mono" type="number" min="0" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="publish-price-input" /></Field>}
    {f.mode === "subasta" && (
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Precio base (CLP)"><Input className="h-11 font-mono" type="number" min="0" value={f.starting_bid} onChange={(e) => set("starting_bid", e.target.value)} data-testid="publish-starting-bid-input" /></Field>
        <Field label="Duración"><Pick value={String(f.auction_hours)} onChange={(v) => set("auction_hours", Number(v))} testId="publish-duration-select" options={[24, 48, 72].map((h) => ({ value: String(h), label: `${h} horas` }))} /></Field>
      </div>
    )}
  </div>
);

const VALID = [
  (f) => !!f.company_id,
  (f) => f.title.trim().length >= 3 && f.category && f.ler_code && f.material_tag.trim().length >= 2 && Number(f.weight) > 0 && f.physical_state && f.weighed_calibrated,
  (f) => f.region && f.comuna,
  () => true,
  (f) => (f.mode === "venta" ? Number(f.price) > 0 : f.mode === "subasta" ? Number(f.starting_bid) > 0 : true),
];

export default function Publish() {
  const api = useApi();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: typeof v === "function" ? v(s[k]) : v }));
  const StepEl = [StepGen, Step1, Step2, Step3, Step4][step];

  const [companies, setCompanies] = useState(null);
  useEffect(() => {
    api("get", "/companies").then(({ data }) => {
      const valid = data.filter((c) => c.status === "validada" && c.role === "generador");
      setCompanies(valid);
      if (valid.length === 1) setF((s) => ({ ...s, company_id: valid[0].id }));
    }).catch(() => setCompanies([]));
  }, [api]);

  const submit = async () => {
    setBusy(true);
    try {
      const { weight, unit, ...rest } = f;
      const body = { ...rest, weight_tons: unit === "kg" ? Number(weight) / 1000 : Number(weight), price: f.price ? Number(f.price) : null, starting_bid: f.starting_bid ? Number(f.starting_bid) : null };
      const { data } = await api("post", "/lots", body);
      toast.success("Lote publicado en el marketplace");
      navigate(`/lotes/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-5 lg:px-8 py-12" data-testid="publish-page">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-2">Publicar lote</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#05130e] mb-8">Libera tu stock en 5 pasos</h1>
      <ol className="grid grid-cols-5 gap-2 mb-8" data-testid="publish-stepper">
        {STEPS.map((s, i) => (
          <li key={s} className="space-y-2">
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-[#00f2a1]" : "bg-slate-200"}`} />
            <p className={`text-[11px] sm:text-xs font-medium flex items-center gap-1 ${i === step ? "text-[#05130e]" : "text-slate-400"}`}>
              {i < step ? <Check className="w-3.5 h-3.5 text-[#00b37a]" /> : <span className="font-mono">0{i + 1}</span>}<span className="hidden sm:inline">{s}</span>
            </p>
          </li>
        ))}
      </ol>
      <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
        <h2 className="font-semibold text-lg mb-6 text-[#05130e]">{STEPS[step]}</h2>
        <StepEl f={f} set={set} companies={companies} />
        <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="publish-back-btn"><ChevronLeft className="w-4 h-4" /> Atrás</Button>
          {step < 4 ? (
            <Button disabled={!VALID[step](f)} onClick={() => setStep(step + 1)} className="rounded-full px-6 font-semibold" data-testid="publish-next-btn">Siguiente <ChevronRight className="w-4 h-4" /></Button>
          ) : (
            <Button disabled={!VALID[4](f) || busy} onClick={submit} className="rounded-full px-6 font-semibold" data-testid="publish-submit-btn">{busy ? "Publicando..." : "Publicar lote"}</Button>
          )}
        </div>
      </div>
    </main>
  );
}
